import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, icons, frames } from "../../Styles";
import { NumberDisplayList, UNIT, EsfStationMCore, EsfStationSizes, WaitAnimation } from "..";
import NewButton from "../BaseComponents/Buttons/newButton";
import { ButtonTypes } from "../../Definitions";
import AnimalNumberDisplay from "../Animals/animalNumberDisplay";
import { useStationById } from "../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { useStationStatusById } from "../../Services/Agrisys_GqlDataCaller/StationCalls/StationStatusSlice";
import { useAnimalById } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { useNavigate } from "react-router";
import { Pages, NavigationFunction as RootNavigation, } from "../../Navigation/root";
import { useAppDispatch, useAppSelector } from "../../Store";
import { dispatchFetchTerminalById } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/LocationFeedingSlices/terminalSlice";
import { fetchStationConsumptionByStationIdsThunk, fetchStationStatusByStationIdsThunk, fetchStationVisitCountByStationIdsThunk } from "../../Store/Thunks/stationStatusByIds";
import { useStationVisitsById } from "../../Services/Agrisys_GqlDataCaller/StationCalls/StationVisitsSlice";
import { useStationVisitCountById } from "../../Services/Agrisys_GqlDataCaller/StationCalls/StationVisitCountSlice";
import { useStationConsumptionById } from "../../Services/Agrisys_GqlDataCaller/StationCalls/StationConsumptionSlice";
import { getFeedMotorConfig } from "./StationLayouts/esfStationCore";
import { EsfStationCoreWalkIn } from "./StationLayouts/esfStationCoreWalkIn";
// TODO: testdata


// stations number background: 
const stationNumberSvg = (
  <svg width="80px" height="80px" viewBox="0 0 100 100">
    <g transform="rotate(-90, 50, 50)">
      <path className="stationNumberSvg" d="M100,100 L0,100 A100,100 0 0 1 100,0 Z" />
    </g>
  </svg>
)

interface StationCard_Props {
  stationId: number;
  onClick: () => void 
}

export default function StationCard({stationId, onClick}:StationCard_Props) {
  const { t } = useTranslation()
  const feedMotorConfig = getFeedMotorConfig(t);
  const navigator = useNavigate();
  const dispatch = useAppDispatch();
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  
  const [isSelected, setIsSelected] = useState(false);
  const { isLoading: isStationLoading, entity: station } = useStationById(stationId);
  const { isLoading: isStationStatusLoading, entity: stationStatus } = useStationStatusById(station?.station_id); 
  const { isLoading: isAnimalLoading, entity: animal } = useAnimalById(stationStatus?.animalId);
  const { isLoading: isStationVisitCountLoading, entity: stationCount } = useStationVisitCountById(station?.station_id); 
  const { isLoading: isStationConsumptionLoading, entity: stationConsumption } = useStationConsumptionById(station?.station_id); 

  useEffect(() => {
    if (!stationId) return;

    const fetchData = () => {
      dispatch(fetchStationStatusByStationIdsThunk([stationId]));
      dispatch(fetchStationConsumptionByStationIdsThunk([stationId]));
      dispatch(fetchStationVisitCountByStationIdsThunk([stationId]));
    };

    fetchData();

    const interval = setInterval(fetchData, 20000);

    return () => clearInterval(interval);
  }, [stationId, dispatch]);
  
  let state: number = 0;
  let content: string | number | JSX.Element = "";
  let secondaryContent: string = "";
  
  if (isStationStatusLoading) {
    content = <WaitAnimation />;
  } else if (!stationStatus) {
    content = t("esfWalkThroughPages.sections.noAnimal", { defaultValue: "No animal" });
  } else {
    const { animalId, responder_number } = stationStatus;
    if (animalId != null) {
      if (isAnimalLoading) {
        content = <WaitAnimation />;
        state = 0;
      } else {
        content = `${t("esfWalkThroughPages.sections.animal", { defaultValue: "Animal" })} ${animal?.animal_number}`;
        state = 1;
      }
      secondaryContent = responder_number ? responder_number.toString() : "";
    } else if (responder_number) {
      state = 2;
      content = t("esfWalkThroughPages.sections.unknownAnimal", { defaultValue: "unknown animal" });
      secondaryContent = responder_number.toString();
    } else {
      content = t("esfWalkThroughPages.sections.noAnimal", { defaultValue: "No animal" });
    }
  }

  // NumberDisplayList: Currently in this section TODO
  const FeedCorrections: JSX.Element = useMemo(() => {
    const fedTodayKg = (stationConsumption?.consumptions.filter(s => s.milestone_day_offset === 0 && s.fk_components !== 1).flatMap(s => s.accumulated_amount).reduce((a, b) => a + b, 0) ?? 0) / 1000
    const usesToday = stationCount?.stationVisitCount.find(s => s.day_offset === 0)?.station_visits
    
    return (
      <NumberDisplayList
        units={[
          <UNIT content={t("units.kg", {defaultValue: "kg"})} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>,
          // <UNIT content={usesToday === 1 ? t("units.time", {defaultValue: "time"}) : t("units.times", {defaultValue: "times"})} color={colors.greyPrimary}/>
        ]}
        values={[
          fedTodayKg,
          // usesToday ?? 0
        ]}
        headers={[
          t("esfWalkThroughPages.sections.fedToday", { defaultValue: "fed today" }),
          // t("esfWalkThroughPages.sections.usesToday", { defaultValue: "uses today" })
        ]}
        height="fit-content"
        gridRows="1fr"
        headersColor={colors.greyPrimary}
        valuesColor={colors.greyDarkPrimary}
      />
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, stationConsumption, stationCount])

  // On pen click
  const handleClick = () => {
    setIsSelected((prev) => !prev);
    onClick();
  };

  const feedMotorStatus = useMemo(() => {
    if (!stationStatus?.motors?.length) return undefined;
  
    const highestStatus = Math.max(...stationStatus.motors.map(m => m.status));
  
    return highestStatus
  }, [stationStatus]);

  return (
    <frames.StationCard onClick={handleClick}>
      <div className="station_firstChild_left">

        <div style={{ gridArea: "1/1/2/2"}}>{FeedCorrections}</div>
        <div style={{ gridArea: "2/1/3/2", ...css.seperatorLine, width: "100%", margin: "0px" }} />
        <div style={{ gridArea: "3/1/4/2", display: "flex", justifyContent: "center"}}>
          <AnimalNumberDisplay
            state={state}
            content={content}
            secondaryContent={secondaryContent}
            header={t("esfWalkThroughPages.sections.inThisStation", { defaultValue: "in this station" })}
            onClick={() => RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animal, replace: true, navArgs: { selected: stationStatus?.animalId }, currentDomain: farm })}
          />
        </div>
      </div>

      <div style={{ gridArea: "1/2/2/3", backgroundColor: colors.greyLightTertiary, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        
        {/* Station Model */}
        <div>{station?.stationType === 2 ? <EsfStationMCore size={EsfStationSizes.small} pins={{ feedMotor: feedMotorStatus, gateMotor: stationStatus?.gateState }} /> : <EsfStationCoreWalkIn size={EsfStationSizes.small} pins={{ feedMotor: feedMotorStatus }} />  }</div>
        
        {/* Station Number */}
        <div style={{ position: "absolute", top: "0", right: "0", zIndex: "1" }}>
          {stationNumberSvg}
        </div>
        <div style={{ position: "absolute", top: "0", right: "5px", zIndex: "2", height: "60px", width: "fit-content", display: "flex", flexDirection: "column", alignItems: "end", justifyContent: "center", gap: "3px" }}>
          <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyPrimary }}>Station</p>  
          <p style={{ ...fonts.header, ...fonts.fontSize1, color: colors.greyDarkPrimary }}>{station?.name}</p>
        </div>
      </div>
    </frames.StationCard>
  )
}